import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import {Exhibit, ExhibitData} from "types/Exhibit";
import {FeatureCollection} from "geojson";
import deserializeExhibit from "services/ngage/deserializers/exhibit";
import {queryString} from "services/string";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

export type ExhibitFilters = {
    search?: string | null
    hidden?: boolean
    published?: boolean
    mapped?: boolean
    pinned?: boolean
    page?: number
    per_page?: number
    sites?: string
}

/**
 * Get all exhibits (paginated)
 */
const all = async (parameters?: ExhibitFilters) => {
    const query = queryString(parameters, {page: 1, per_page: 100})
    const response = await client.get<DataPage<Exhibit> | null>(`/exhibits?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeExhibit)
    }

    return response.data
}

/**
 * Get all exhibits (paginated)
 */
const filtered = async (filters: ExhibitFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
    const response = await client.get<DataResource<Exhibit>>('/exhibits', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeExhibit)
    }

    return response.data
}

/**
 * Get all exhibit positions as a GeoJSON feature collection
 */
const positions = async (excludedIds?: Array<number>) => {
    const exhibits = await all({mapped: true})
    const filteredExhibits = exhibits?.data.filter(exhibit => !excludedIds?.includes(exhibit.id)) || []

    return {
        type: "FeatureCollection",
        features: filteredExhibits.map(exhibit => ({
            id: exhibit.id,
            type: "Feature",
            properties: {
                type: exhibit.type.name,
                name: exhibit.name,
                caption: exhibit.caption,
                description: exhibit.description,
                zone: exhibit.zone.name,
            },
            geometry: exhibit.position
        }))
    } as FeatureCollection
}

/**
 * Get a single exhibit
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<Exhibit | null>(`/exhibits/${id}`)
    return response.data ? deserializeExhibit(response.data) : null
}

/**
 * Store a new exhibit
 *
 * @param data
 */
const store = async (data: ExhibitData) => {
    const response = await client.post<Exhibit | null>('/exhibits', data)
    return response.data ? deserializeExhibit(response.data) : null
}

/**
 * Update an existing exhibit
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: ExhibitData) => {
    const response = await client.put<Exhibit | null>(`/exhibits/${id}`, data)
    return response.data ? deserializeExhibit(response.data) : null
}

/**
 * Permanently delete a exhibit
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/exhibits/${id}`)
    return response.status === 204
}

const exhibits = {
    all,
    filtered,
    positions,
    get,
    store,
    update,
    destroy
}

export default exhibits;